import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useInViewport } from 'ahooks';
import { useTracker } from '@archipro-website/tracker';

interface RevampModuleSectionProps {
    children: ReactNode;
    /** Read progress the module represents, 0–1 (legacy DetailSection parity). */
    percentContentViewed: number;
    moduleId: string;
    className?: string;
}

/**
 * Section wrapper for a revamp body module carrying the legacy DetailSection's
 * scroll-depth tracking: one `PageModuleViewed` per module per page view, on
 * first scroll into view, with `ExtraData` = percent content viewed. Same
 * event name, payload and dedupe as legacy so the Snowflake series stays
 * continuous across the revamp rollout.
 */
export const RevampModuleSection = ({
    children,
    percentContentViewed,
    moduleId,
    className,
}: RevampModuleSectionProps) => {
    const tracker = useTracker();
    const moduleRef = useRef<HTMLElement>(null);

    const [inView] = useInViewport(moduleRef);
    const eventFired = useRef(false);

    useEffect(() => {
        if (!inView || eventFired.current) return;
        tracker.log('PageModuleViewed', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: percentContentViewed,
            },
            ga4Data: { percentContentViewed },
        });
        eventFired.current = true;
    }, [inView, tracker, percentContentViewed]);

    return (
        <section ref={moduleRef} id={moduleId} className={className}>
            {children}
        </section>
    );
};
